import IdTokenVerifier from "idtoken-verifier";
import {
  assertMasqueradeAuth0ConfigIsValid,
  getCustomDomain,
  isMasqueradeAuth0Config,
} from "../config/masqueradeAuth0Config";
import {
  DecodedJwt,
  IAuth0Config,
  IMasqueradeAuth0Config,
} from "../interfaces";

/**
 * JWT segments are base64*url* encoded, so they may contain "-" and "_", which
 * bare atob() rejects. Mirrors auth0-spa-js's own urlDecodeB64 so we decode
 * exactly the tokens it does. (base64UrlToHex below already did this for the
 * signature; the payload decode did not.)
 *
 * The UTF-8 reassembly is not optional. `atob` returns one Latin-1 character
 * per *byte*, so a multi-byte claim - any accented or non-Latin name - decodes
 * to mojibake ("Renée" -> "RenÃ©e") rather than throwing, because UTF-8
 * continuation bytes are legal unescaped JSON string characters. The
 * percent-encode-then-decodeURIComponent round trip is spa-js's own decodeB64
 * verbatim; TextDecoder would be equivalent but is absent under jsdom.
 */
export const urlDecodeB64 = (input: string): string =>
  decodeURIComponent(
    atob(input.replace(/_/g, "/").replace(/-/g, "+"))
      .split("")
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join("")
  );

export const decodeJwt = (jwt: string): DecodedJwt => {
  try {
    const header = JSON.parse(urlDecodeB64(jwt.split(".")[0]));
    const payload = JSON.parse(urlDecodeB64(jwt.split(".")[1]));
    return {
      header,
      payload,
    };
  } catch (e) {
    throw new Error("Could not decode jwt");
  }
};

class MemoryJwksCache {
  private store = new Map<string, unknown>();
  has(key: string) {
    return this.store.has(key);
  }
  get(key: string) {
    return this.store.get(key);
  }
  set(key: string, value: unknown) {
    this.store.set(key, value);
    return value;
  }
}

const verifierCache = new Map<string, IdTokenVerifier>();

// Ordinary login still supplies IAuth0Config; masquerade supplies its environment.
const getVerifierCustomDomain = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): string =>
  isMasqueradeAuth0Config(auth0Config)
    ? getCustomDomain(auth0Config.environment)
    : auth0Config.customDomain;

// idtoken-verifier is built for OIDC ID tokens: when `aud` has more than one
// value it requires `azp` to equal the configured audience. Real Auth0
// *access* tokens legitimately have both a custom API audience and the
// userinfo URL in `aud` (whenever `openid` scope is requested), with `azp`
// set to the client ID instead - so its own verify() rejects every real
// access token. We only borrow its JWKS fetching/caching and raw RSA
// signature check (getRsaVerifier), and do our own access-token-appropriate
// claim checks (below) instead of its ID-token claim orchestration.
const getVerifier = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): IdTokenVerifier => {
  const customDomain = getVerifierCustomDomain(auth0Config);
  const cacheKey = `${customDomain}::${auth0Config.audience}`;
  const cached = verifierCache.get(cacheKey);
  if (cached) return cached;
  const verifier = new IdTokenVerifier({
    issuer: `https://${customDomain}/`,
    audience: auth0Config.audience,
    jwksURI: `https://${customDomain}/.well-known/jwks.json`,
    jwksCache: new MemoryJwksCache(),
  });
  verifierCache.set(cacheKey, verifier);
  return verifier;
};

function base64UrlToHex(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  let hex = "";
  for (let i = 0; i < binary.length; i++) {
    hex += binary.charCodeAt(i).toString(16).padStart(2, "0");
  }
  return hex;
}

interface RsaVerifier {
  verify(data: string, signature: string): boolean;
}

type GetRsaVerifier = (
  iss: string,
  kid: string,
  cb: (err: Error | null, rsaVerifier?: RsaVerifier) => void
) => void;

/**
 * Claim checks common to access tokens and ID tokens - everything except the
 * audience rule, which differs between the two (see the two assertions below).
 * No cryptographic check here; verifySignature does that separately.
 */
function assertCommonClaims(
  decoded: DecodedJwt,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): void {
  if (auth0Config == null || !("customDomain" in auth0Config)) {
    assertMasqueradeAuth0ConfigIsValid(auth0Config);
  }
  if (decoded.header.alg !== "RS256") {
    throw new Error(
      `Signature algorithm of "${decoded.header.alg}" is not supported. Expected the token to be signed with "RS256".`
    );
  }
  if (!decoded.payload.sub) {
    throw new Error(
      "Subject (sub) claim must be a string present in the token"
    );
  }
  if (isNaN(decoded.payload.exp)) {
    throw new Error(
      "Expiration Time (exp) claim must be a number present in the token"
    );
  }
  if (isNaN(decoded.payload.iat)) {
    throw new Error(
      "Issued At (iat) claim must be a number present in the token"
    );
  }

  const leeway = 60;
  const now = new Date(Date.now());
  const expDate = new Date(0);
  expDate.setUTCSeconds(decoded.payload.exp + leeway);
  if (now > expDate) {
    throw new Error(
      `Expiration Time (exp) claim error in the token; the token is expired`
    );
  }

  const expectedIssuer = `https://${getVerifierCustomDomain(auth0Config)}/`;
  if (decoded.payload.iss !== expectedIssuer) {
    throw new Error(
      `Issuer (iss) claim mismatch in the token; expected "${expectedIssuer}" but found "${decoded.payload.iss}"`
    );
  }
}

const audiencesOf = (decoded: DecodedJwt): string[] =>
  Array.isArray(decoded.payload.aud)
    ? decoded.payload.aud
    : [decoded.payload.aud];

/**
 * Validates claim shape/values only - no cryptographic check. Mirrors
 * OIDC access-token requirements, not idtoken-verifier's ID-token rules
 * (see getVerifier above for why we don't use its own claim checks).
 */
function assertClaimsAreValid(
  decoded: DecodedJwt,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): void {
  assertCommonClaims(decoded, auth0Config);
  if (!audiencesOf(decoded).includes(auth0Config.audience)) {
    throw new Error(
      `Audience (aud) claim mismatch in the token; expected "${auth0Config.audience}" but found "${decoded.payload.aud}"`
    );
  }
}

/**
 * Validates ID-token claims against the expected client ID, not the API audience.
 * Checks azp when multiple audiences are present. Nonce validation is omitted
 * for server-to-server tokens, where no browser-generated nonce is available.
 */
function assertIdTokenClaimsAreValid(
  decoded: DecodedJwt,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config,
  expectedAudience: string
): void {
  assertCommonClaims(decoded, auth0Config);
  const audiences = audiencesOf(decoded);
  if (!audiences.includes(expectedAudience)) {
    throw new Error(
      `Audience (aud) claim mismatch in the id token; expected "${expectedAudience}" but found "${decoded.payload.aud}"`
    );
  }
  // With more than one audience, OIDC requires azp to name the intended client.
  if (audiences.length > 1 && decoded.payload.azp !== expectedAudience) {
    throw new Error(
      `Authorized Party (azp) claim mismatch in the id token; expected "${expectedAudience}" but found "${decoded.payload.azp}"`
    );
  }
}

/**
 * Cryptographic check only, via idtoken-verifier's JWKS-backed RSA
 * primitive - assertClaimsAreValid has already run by the time this
 * is called from verifyJwt.
 */
function verifySignature(
  jwt: string,
  decoded: DecodedJwt,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): Promise<void> {
  const verifier = getVerifier(auth0Config) as unknown as {
    getRsaVerifier: GetRsaVerifier;
  };
  const [header, payload, signature] = jwt.split(".");
  const signatureHex = base64UrlToHex(signature);

  return new Promise<void>((resolve, reject) => {
    verifier.getRsaVerifier(
      decoded.payload.iss,
      decoded.header.kid,
      (err, rsaVerifier) => {
        if (err) {
          reject(
            new Error(`Jwt signature verification failed: ${err.message}`)
          );
          return;
        }
        if (!rsaVerifier!.verify(`${header}.${payload}`, signatureHex)) {
          reject(
            new Error("Jwt signature verification failed: Invalid signature")
          );
          return;
        }
        resolve();
      }
    );
  });
}

export const verifyJwt = async (
  jwt: string,
  decoded: DecodedJwt,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): Promise<void> => {
  assertClaimsAreValid(decoded, auth0Config);
  await verifySignature(jwt, decoded, auth0Config);
};

/**
 * Verifies ID-token claims and the JWKS-backed signature.
 * The expected audience defaults to the SPA's client ID; masquerade callers
 * pass the minting client's ID instead. Those tokens are for the local session
 * only, not for forwarding to services expecting the SPA's audience.
 */
export const verifyIdToken = async (
  jwt: string,
  decoded: DecodedJwt,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config,
  expectedAudience: string = auth0Config?.clientId
): Promise<void> => {
  assertIdTokenClaimsAreValid(decoded, auth0Config, expectedAudience);
  await verifySignature(jwt, decoded, auth0Config);
};
